import {
  Beneficiary,
  ExtendedCoverageInfo,
  IEPStatus,
  NewToMedicareAlertInfo,
  MedicarePartInfo,
  PlanType,
  OecStatus,
  OecStatuses,
  MedigapCoverage,
} from "../@types";
import { addMonths, addYears, lastDayOfMonth, startOfMonth } from "date-fns";
import { isValidApiDate } from "./dateHelpers";
import { AppContext } from "../app/store";
import { Dispatch, useContext } from "react";
import { useLocation, matchPath } from "react-router";
import { Location } from "history";
import {
  EnrollmentPages,
  hasGlobalSession,
  isMedicareAdvantageType,
  parseSearchParams,
  planTypeHasDrugCoverage,
} from ".";
import routes from "../app/routes";
import URI from "urijs";
import { TranslationKey } from "./intlHooks";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMOEPAlert } from "./useMOEPAlert";
import { HelpDrawerAnalytics } from "../components/HelpDrawerWrapper";
import {
  AnalyticsActionType,
  AnalyticsActions,
  Ga4Event,
} from "../app/contexts/Analytics/types";

export const newToMedicareHelpDrawerAnalytics = ({
  heading,
  text,
  dispatch,
}: {
  heading: string;
  text: string;
  dispatch: Dispatch<AnalyticsActions>;
}): HelpDrawerAnalytics => {
  return {
    openDrawer: () => {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_category: "ui interaction",
          event_action: "help drawer opened",
          event_label: heading,
        },
      });
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.HELP_DRAWER_OPENED,
          heading: heading,
          text: text,
        },
      });
    },
    closeDrawer: () => {},
  };
};

export const createNewToMedicareAlertInfo = ({
  currentDate,
  bene,
  oecStatuses = { currentYear: undefined, nextYear: undefined },
  medigapCoverages = [],
}: {
  currentDate: Date;
  bene: Beneficiary;
  oecStatuses?: OecStatuses;
  medigapCoverages: MedigapCoverage[];
}): NewToMedicareAlertInfo => {
  const birthdate = isValidApiDate(bene.birthdate)
    ? new Date(bene.birthdate)
    : undefined;

  const iepStatus = getIEPStatusFromAPI(
    bene.medicare_info.iep_start_date,
    bene.medicare_info.iep_end_date
  );

  const hasMedigap = medigapCoverages.length > 0;

  const pendingPlans: OecStatus[] = [];
  if (oecStatuses.currentYear) {
    pendingPlans.push(oecStatuses.currentYear);
  }
  if (oecStatuses.nextYear) {
    pendingPlans.push(oecStatuses.nextYear);
  }

  const hasPDPPlan =
    containsPDPPlan(bene.current_coverage) ||
    containsPDPPlan(bene.future_coverage) ||
    containsPDPPlan(pendingPlans);
  const hasMAPDPlan =
    containsMAPDPlan(bene.current_coverage) ||
    containsMAPDPlan(bene.future_coverage) ||
    containsMAPDPlan(pendingPlans);
  const hasPartAandB =
    hasMedicarePart(currentDate, bene.medicare_info.part_a) &&
    hasMedicarePart(currentDate, bene.medicare_info.part_b);

  return {
    birthdate,
    iepStatus,
    newToMedicareResult: undefined,
    hasMedigap,
    hasPDPPlan,
    hasMAPDPlan,
    hasPartAandB,
  };
};

/**
 * Calculates the Initial Enrollment Period (IEP) for a bene
 * @param birthdate the birthdate
 * @returns the Initial Enrollment Period
 */
export const calculateIEP = (
  birthdate: Date
): {
  startDate: Date;
  endDate: Date;
} => {
  // offsets are in months
  let startOffset = -3;
  let endOffset = 3;

  if (birthdate.getDate() === 1) {
    // birthdate is the first day of the month
    startOffset = -4;
    endOffset = 2;
  }
  const MEDICARE_ELIGIBILITY_AGE = 65;
  const firstDay = startOfMonth(birthdate);
  const sixyFiveDate = addYears(firstDay, MEDICARE_ELIGIBILITY_AGE);
  const iepStart = addMonths(sixyFiveDate, startOffset);
  const iepEnd = lastDayOfMonth(addMonths(sixyFiveDate, endOffset));

  return {
    startDate: iepStart,
    endDate: iepEnd,
  };
};

/**
 * Gets the IEPStatus from the API IEP dates
 * @param iepStart API IEP start date
 * @param iepEnd API IEP end end
 * @returns IEPStatus or undefined if the IEP dates are invalid
 */
export const getIEPStatus = (
  iepStartDate: Date,
  iepEndDate: Date
): IEPStatus => {
  const currentDate = new Date();
  if (currentDate < iepStartDate) {
    return IEPStatus.IEP_STATUS_BEFORE_IEP;
  }
  if (currentDate > iepEndDate) {
    return IEPStatus.IEP_STATUS_PAST_IEP;
  }
  return IEPStatus.IEP_STATUS_WITHIN_IEP;
};

/**
 * Gets the IEPStatus from the API IEP dates
 * @param iepStart API IEP start date
 * @param iepEnd API IEP end end
 * @returns IEPStatus or undefined if the IEP dates are invalid
 */
export const getIEPStatusFromAPI = (
  iepStart: string,
  iepEnd: string
): IEPStatus | undefined => {
  if (isValidApiDate(iepStart) && isValidApiDate(iepEnd)) {
    const iepStartDate = new Date(iepStart);
    const iepEndDate = new Date(iepEnd);
    return getIEPStatus(iepStartDate, iepEndDate);
  }
  return undefined;
};

export const hasPlanType = (
  coverageInfo: (ExtendedCoverageInfo | OecStatus)[],
  planType: PlanType
): boolean => {
  return !!coverageInfo.find(coverage => coverage.plan_type === planType);
};

export const containsMAPDPlan = (
  coverageInfo: (ExtendedCoverageInfo | OecStatus)[]
): boolean => {
  return hasPlanType(coverageInfo, PlanType.MAPD);
};

export const containsPDPPlan = (
  coverageInfo: (ExtendedCoverageInfo | OecStatus)[]
): boolean => {
  return hasPlanType(coverageInfo, PlanType.PDP);
};

export const hasMedicarePart = (
  currentDate: Date,
  partInfo: MedicarePartInfo
): boolean => {
  if (isValidApiDate(partInfo.start_date)) {
    const partStartDate = new Date(partInfo.start_date);

    if (partInfo.stop_date === "") {
      return true;
    } else if (isValidApiDate(partInfo.stop_date)) {
      const partEndDate = new Date(partInfo.stop_date);
      return partEndDate > partStartDate && !(partEndDate < currentDate);
    }
  }
  return false;
};

type RouteKeys = keyof typeof routes;
type SummaryRouteKeys = keyof typeof routes.summary;
type MedigapRoutes = keyof typeof routes.medigap;
export type PossibleRoutes =
  | (typeof routes)[RouteKeys]
  | (typeof routes.summary)[SummaryRouteKeys]
  | (typeof routes.medigap)[MedigapRoutes];

export const isNtmAlertPathMatch = ({
  route,
  location,
}: {
  route: PossibleRoutes;
  location: Location;
}) =>
  !!matchPath(location.pathname, {
    path: route as string,
    exact: true,
    strict: true,
  });

export const getIsSepPage = (location: Location) => {
  const uri = new URI(location.search);
  const searchParams = parseSearchParams(uri.search(true));
  return (
    isNtmAlertPathMatch({ route: routes.enroll, location }) &&
    searchParams.page === EnrollmentPages.Sep
  );
};

export const getNeedsMedicareABAlert = ({
  hasPartAandB,
  newToMedicareResult,
  isLoggedIn,
  location,
  planType,
  tmpFeEnableNewToMedicare,
}: {
  hasPartAandB: NewToMedicareAlertInfo["hasPartAandB"] | undefined;
  newToMedicareResult:
    | NewToMedicareAlertInfo["newToMedicareResult"]
    | undefined;
  isLoggedIn: boolean;
  location: Location;
  planType: PlanType | undefined;
  tmpFeEnableNewToMedicare: boolean;
}): {
  bodyKey: TranslationKey;
  headingKey: TranslationKey;
  shouldShow: boolean;
  showAsError: boolean;
} => {
  let shouldShow = false;
  let showAsError = false;
  // default to Search Results keys
  let headingKey: TranslationKey =
    "ntm.alert.parts_ab_required.heading.search_results_page";
  let bodyKey: TranslationKey =
    "ntm.alert.parts_ab_required.body.search_results_page";
  if (!tmpFeEnableNewToMedicare) {
    return { bodyKey, headingKey, shouldShow, showAsError };
  }

  switch (true) {
    case isNtmAlertPathMatch({ route: routes.summary.landingPage, location }):
      shouldShow = !hasPartAandB;
      headingKey = "ntm.alert.parts_ab_required.heading.summary_page";
      bodyKey = "ntm.alert.parts_ab_required.body.summary_page";
      break;
    case isNtmAlertPathMatch({ route: routes.searchResults, location }):
      shouldShow = isLoggedIn
        ? !!planType && isMedicareAdvantageType(planType) && !hasPartAandB
        : !!newToMedicareResult;
      break;
    case getIsSepPage(location):
      // Only needed on the Sep step of enrollment, when that step is rendered
      if (isLoggedIn) {
        shouldShow =
          !!planType && isMedicareAdvantageType(planType) && !hasPartAandB;
        showAsError = true;
        headingKey = "ntm.alert.parts_ab_required.heading.enrollment_sep_page";
        bodyKey = "ntm.alert.parts_ab_required.body.enrollment_sep_page";
      } else {
        shouldShow = !!newToMedicareResult;
      }
      break;
    case isNtmAlertPathMatch({ route: routes.medigap.plans, location }):
      shouldShow = isLoggedIn ? !hasPartAandB : !!newToMedicareResult;
      headingKey = "ntm.alert.parts_ab_required.heading.medigap";
      bodyKey = "ntm.alert.parts_ab_required.body.medigap";
      break;
    case isNtmAlertPathMatch({ route: routes.medigap.policies, location }):
      shouldShow = isLoggedIn ? !hasPartAandB : !!newToMedicareResult;
      headingKey = "ntm.alert.parts_ab_required.heading.medigap";
      bodyKey = "ntm.alert.parts_ab_required.body.medigap";
      break;
    default:
      break;
  }
  return { shouldShow, showAsError, headingKey, bodyKey };
};

export const useNeedsMedicareABAlert = () => {
  const {
    state,
    state: { newToMedicareAlertInfo, planType },
  } = useContext(AppContext);
  const location = useLocation();
  const isLoggedIn = hasGlobalSession(state);
  const { tmpFeEnableNewToMedicare } = useFlags();
  return getNeedsMedicareABAlert({
    hasPartAandB: newToMedicareAlertInfo?.hasPartAandB,
    newToMedicareResult: newToMedicareAlertInfo?.newToMedicareResult,
    isLoggedIn,
    planType,
    location,
    tmpFeEnableNewToMedicare,
  });
};

/**
 * Rendering and content logic for Pard D LEP Alerts, for NtM initiative
 * @param args
 * @param args.isSepEnrollmentAlert - Whether to render special content on enrollment SEP section
 * @param args.isIepEnrollmentAlert - Whether to render special content on enrollment IEP section
 */
export const getAvoidPartDPenaltyAlertContent = ({
  iepStatus,
  isLoggedIn,
  isSepEnrollmentAlert = false,
  isIepEnrollmentAlert = false,
  location,
}: {
  iepStatus: IEPStatus | undefined;
  isLoggedIn: boolean;
  isIepEnrollmentAlert?: boolean;
  isSepEnrollmentAlert?: boolean;
  location: Location;
}): {
  bodyKey: TranslationKey;
  renderHeading: boolean;
} => {
  const isSepPage = getIsSepPage(location);
  let renderHeading = !isSepPage;
  // Alert body depends on LI/anon and iepStatus, default to generic for anon
  let bodyKey: TranslationKey = "ntm.alert.avoid_part_d_penalty.body.generic";
  if (isSepPage) {
    renderHeading = false;
    if (isSepEnrollmentAlert) {
      bodyKey = "ntm.alert.avoid_part_d_penalty.body.enrollment_sep";
    }
    if (isIepEnrollmentAlert) {
      bodyKey = "ntm.alert.avoid_part_d_penalty.body.enrollment_iep";
    }
  } else {
    if (isLoggedIn) {
      switch (iepStatus) {
        case IEPStatus.IEP_STATUS_WITHIN_IEP:
          bodyKey = "ntm.alert.avoid_part_d_penalty.body.within_iep";
          break;
        case IEPStatus.IEP_STATUS_PAST_IEP:
          bodyKey = "ntm.alert.avoid_part_d_penalty.body.past_iep";
          break;
        case IEPStatus.IEP_STATUS_BEFORE_IEP:
          bodyKey = "ntm.alert.avoid_part_d_penalty.body.before_iep";
          break;
      }
    }
  }
  return {
    bodyKey,
    renderHeading,
  };
};

/**
 * Hook providing rendering and content logic for Pard D LEP Alerts, for NtM initiative
 * @param args
 * @param args.isSepEnrollmentAlert - Whether to render special content on enrollment SEP section
 * @param args.isIepEnrollmentAlert - Whether to render special content on enrollment IEP section
 */
export const useAvoidPartDPenaltyAlertContent = ({
  isSepEnrollmentAlert = false,
  isIepEnrollmentAlert = false,
}: {
  isSepEnrollmentAlert?: boolean;
  isIepEnrollmentAlert?: boolean;
} = {}) => {
  const {
    state,
    state: { newToMedicareAlertInfo },
  } = useContext(AppContext);
  const isLoggedIn = hasGlobalSession(state);
  const location = useLocation();
  return getAvoidPartDPenaltyAlertContent({
    iepStatus: newToMedicareAlertInfo?.iepStatus,
    isLoggedIn,
    isIepEnrollmentAlert,
    isSepEnrollmentAlert,
    location,
  });
};

// @TODO - New helper and hook added to handle rendering and content logic for
// these alerts. These and the associated tests should be removed if no code is
// referencing. Existing test logic should be re-used (if it's correct) and expanded
// for tests of the new helper, `getAvoidPartDPenaltyAlert`
export const shouldShowAvoidPartDPenaltyAlert = ({
  isLoggedIn,
  location,
  newToMedicareAlertInfo,
  planType,
  tmpFeEnableNewToMedicare,
}: {
  isLoggedIn: boolean;
  location: Location;
  newToMedicareAlertInfo: NewToMedicareAlertInfo | undefined;
  planType: PlanType | undefined;
  tmpFeEnableNewToMedicare: boolean;
}): boolean => {
  let shouldShow = false;
  if (!tmpFeEnableNewToMedicare) {
    return false;
  }
  const isSepPage = getIsSepPage(location);
  const {
    hasMedigap,
    hasPartAandB,
    hasPDPPlan,
    hasMAPDPlan,
    newToMedicareResult,
  } = newToMedicareAlertInfo || {
    hasMedigap: false,
    hasPartAandB: false,
    hasPDPPlan: false,
    hasMAPDPlan: false,
    newToMedicareResult: undefined,
  };
  const isSummaryPage = isNtmAlertPathMatch({
    route: routes.summary.landingPage,
    location,
  });
  const isPlanDetailsPage = isNtmAlertPathMatch({
    route: routes.planDetails,
    location,
  });
  const isSearchResults = isNtmAlertPathMatch({
    route: routes.searchResults,
    location,
  });
  if (isSepPage) {
    shouldShow = !!newToMedicareResult;
  } else {
    if (isLoggedIn) {
      const shouldShowOnSummaryPage =
        !hasPDPPlan &&
        !hasMAPDPlan &&
        ((hasMedigap && hasPartAandB) ||
          (!hasMedigap && hasPartAandB) ||
          (!hasMedigap && !hasPartAandB));
      shouldShow = isSummaryPage
        ? shouldShowOnSummaryPage
        : (isSearchResults || isPlanDetailsPage) && !hasMAPDPlan && !hasPDPPlan;
    } else {
      shouldShow =
        !!newToMedicareResult &&
        (isSearchResults ||
          (isPlanDetailsPage &&
            !!planType &&
            planTypeHasDrugCoverage(planType)));
    }
  }
  return shouldShow;
};

export const useShowAvoidPartDPenaltyAlert = (): boolean => {
  const {
    state,
    state: { newToMedicareAlertInfo, planType },
  } = useContext(AppContext);
  const isLoggedIn = hasGlobalSession(state);
  const location = useLocation();
  const { tmpFeEnableNewToMedicare } = useFlags();
  return shouldShowAvoidPartDPenaltyAlert({
    isLoggedIn,
    location,
    newToMedicareAlertInfo,
    planType,
    tmpFeEnableNewToMedicare,
  });
};

export const useShowNewToMedicareCta = (): boolean => {
  const { tmpFeEnableNewToMedicare } = useFlags();
  const { shouldShow: showNeedsMedicareAbNtmAlert } = useNeedsMedicareABAlert();
  const showAvoidPartDPenaltyNtmAlert = useShowAvoidPartDPenaltyAlert();
  const { showMOEPMessaging: showMedigapNtmAlert } = useMOEPAlert();
  const showNewToMedicareCta =
    tmpFeEnableNewToMedicare &&
    (showNeedsMedicareAbNtmAlert ||
      showAvoidPartDPenaltyNtmAlert ||
      showMedigapNtmAlert);

  return showNewToMedicareCta;
};
